// Voice allocation: maps a StrikeIntent to a concrete engine voice slot, reusing free
// slots first and otherwise stealing the shortest-remaining/quietest tail. Resolves the
// diatom's modes via geometryToModes.
import type { StrikeIntent, DispatchedStrike } from '@shared/intents';
import type { DiatomDescriptor, StrikePos } from '@shared/diatom';
import { geometryToModes } from '../diatoms/geometryToModes';

interface VoiceSlot {
  voiceId: number;
  busyUntil: number; // ctx time when the voice's ring is effectively done
  lastRms: number;
}

export class VoiceAllocator {
  private slots: VoiceSlot[];
  private strikeCounter = 0;
  private ratioFamily: string | undefined;

  constructor(
    maxVoices: number,
    private readonly fs: number,
    private readonly modesCap: number,
    private readonly byId: (id: string) => DiatomDescriptor | undefined,
  ) {
    this.slots = Array.from({ length: maxVoices }, (_, i) => ({
      voiceId: i,
      busyUntil: 0,
      lastRms: 0,
    }));
  }

  /** Set the active sonic base (preset-driven) used when resolving modes. */
  setRatioFamily(family: string | undefined): void {
    this.ratioFamily = family;
  }

  /** Update a voice's measured RMS (from the engine envelope) to inform stealing. */
  observeRms(voiceId: number, rms: number): void {
    const s = this.slots[voiceId];
    if (s) s.lastRms = rms;
  }

  /**
   * Allocate a slot for an intent and resolve its modes. Returns null if the diatom is unknown.
   * `bowedVoiceIds` (currently-sustaining bows) are protected from reuse/stealing so a held
   * drone isn't yanked away mid-note.
   */
  dispatch(intent: StrikeIntent, bowedVoiceIds?: ReadonlySet<number>): DispatchedStrike | null {
    const desc = this.byId(intent.diatomId);
    if (!desc) return null;

    const now = intent.whenSec;
    const isBowed = (s: VoiceSlot) => bowedVoiceIds?.has(s.voiceId) ?? false;
    // free slot first (never a sustaining bowed voice)
    let chosen = this.slots.find((s) => s.busyUntil <= now && !isBowed(s));
    if (!chosen) {
      // steal the slot whose ring ends soonest (quietest tie-break), preferring non-bowed
      const pool = this.slots.filter((s) => !isBowed(s));
      const candidates = pool.length ? pool : this.slots;
      chosen = candidates.reduce((best, s) =>
        s.busyUntil < best.busyUntil ||
        (s.busyUntil === best.busyUntil && s.lastRms < best.lastRms)
          ? s
          : best,
      );
    }

    const strikePos: StrikePos = {
      r: Math.min(1, Math.hypot(intent.position.x - 0.5, intent.position.y - 0.5) * 2),
      theta: Math.atan2(intent.position.y - 0.5, intent.position.x - 0.5),
    };
    const modes = geometryToModes(desc, intent.pitchHz, {
      fs: this.fs,
      maxModes: this.modesCap,
      strikePos,
      strikeCounter: this.strikeCounter++,
      ratioFamily: this.ratioFamily,
    });
    // apply mood decay scaling
    if (intent.decayScale !== 1) {
      for (const m of modes) m.t60 *= intent.decayScale;
    }

    const maxT60 = modes.reduce((mx, m) => Math.max(mx, m.t60), 0);
    chosen.busyUntil = now + maxT60;
    return { intent, voiceId: chosen.voiceId, modes };
  }
}
